import { DateFieldFormat } from 'garaje/utils/enums';
import IMask, { type MaskedRangeOptions } from 'imask';

interface DateFormatConfig {
  format: DateFieldFormat;
  mask: string;
  placeholder: string;
  blocks: Record<string, MaskedRangeOptions>;
  parse: (value: string) => { day: number; month: number; year: number } | null;
}

export function isValidDate(day: number, month: number, year: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export function validateDateString(value: string, format: DateFieldFormat): boolean {
  const config = getDateMaskConfig(format);
  const parsed = config.parse(value);

  if (!parsed) {
    return false;
  }

  return isValidDate(parsed.day, parsed.month, parsed.year);
}

/**
 * Detects the user's preferred date format based on browser locale.
 * Uses Intl.DateTimeFormat to determine the order of date parts.
 */
export function getBrowserLocaleDateFormat(): DateFieldFormat {
  const locale = navigator.language || 'en-US';

  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(new Date(2000, 11, 31));
    const order = parts.filter((part) => ['year', 'month', 'day'].includes(part.type)).map((part) => part.type);

    const separator = parts.find((part) => part.type === 'literal')?.value || '/';

    if (order[0] === 'year') {
      return DateFieldFormat.YYYY_MM_DD;
    } else if (order[0] === 'day' && separator === '.') {
      return DateFieldFormat.DD_DOT_MM_YYYY;
    } else if (order[0] === 'day') {
      return DateFieldFormat.DD_MM_YYYY;
    } else {
      return DateFieldFormat.MM_DD_YYYY;
    }
  } catch {
    return DateFieldFormat.MM_DD_YYYY;
  }
}

export function getDateMaskConfig(format: DateFieldFormat): DateFormatConfig {
  const baseBlocks = {
    DD: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 31,
      maxLength: 2,
    },
    MM: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      maxLength: 2,
    },
    YYYY: {
      mask: IMask.MaskedRange,
      from: 1900,
      to: 2100,
      maxLength: 4,
    },
  };

  const configs: Record<DateFieldFormat, DateFormatConfig> = {
    [DateFieldFormat.MM_DD_YYYY]: {
      format: DateFieldFormat.MM_DD_YYYY,
      mask: 'MM/DD/YYYY',
      placeholder: '__/__/____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return null;
        return { month: parseInt(match[1]!, 10), day: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.DD_MM_YYYY]: {
      format: DateFieldFormat.DD_MM_YYYY,
      mask: 'DD/MM/YYYY',
      placeholder: '__/__/____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return null;
        return { day: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.YYYY_MM_DD]: {
      format: DateFieldFormat.YYYY_MM_DD,
      mask: 'YYYY-MM-DD',
      placeholder: '____-__-__',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;
        return { year: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), day: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.DD_DOT_MM_YYYY]: {
      format: DateFieldFormat.DD_DOT_MM_YYYY,
      mask: 'DD.MM.YYYY',
      placeholder: '__.__.____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!match) return null;
        return { day: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
  };

  // All DateFieldFormat values are covered in configs, so this is always defined
  return configs[format];
}

export function convertToISO(value: string, format: DateFieldFormat): string | null {
  const config = getDateMaskConfig(format);
  const parsed = config.parse(value);
  if (!parsed) return null;

  const { day, month, year } = parsed;
  if (!isValidDate(day, month, year)) return null;

  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export function convertFromISO(isoString: string, format: DateFieldFormat): string | null {
  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = match[1]!;
  const month = match[2]!;
  const day = match[3]!;

  switch (format) {
    case DateFieldFormat.MM_DD_YYYY:
      return `${month}/${day}/${year}`;
    case DateFieldFormat.DD_MM_YYYY:
      return `${day}/${month}/${year}`;
    case DateFieldFormat.YYYY_MM_DD:
      return `${year}-${month}-${day}`;
    case DateFieldFormat.DD_DOT_MM_YYYY:
      return `${day}.${month}.${year}`;
  }
}

export function getBrowserDateMaskConfig(): DateFormatConfig {
  const format = getBrowserLocaleDateFormat();
  return getDateMaskConfig(format);
}
